/*
 * delivery-context.js — 배송 국가 [컨텍스트 계층]
 * ---------------------------------------------------------------------------
 * Market(통화·상품·SEO)은 그대로 두고, **배송 정책만** 방문자의 실제 접속 국가로 교정한다.
 * localization.country 는 마켓 기본국(현재 US)에 고정돼 있어, 한국 방문자에게 "$74.99 이상 무료배송"
 * 이라고 표시되지만 실제 KR 임계금액은 $19.99 다. 이 거짓을 없애는 것이 이 모듈의 전부다.
 *
 * 서버는 방문자 국가를 알 수 없다 — Liquid `request` 에 geo 가 없고, 머천트가 Shopify 앞단에
 * 엣지를 둘 수도 없다. 그래서 서버는 마켓 기본값으로 렌더하고 여기서 표시만 교정한다.
 *
 * 계층: [데이터] delivery-matrix ─ [계산] delivery-estimate ─ [UI] delivery-estimate-ui
 *       └ [컨텍스트] delivery-context  ← 국가 결정 + 사이트 전역 적용 (이 파일)
 *
 * 국가 결정 우선순위
 *   1. localStorage 'wookie_ship_country'       사용자의 명시적 선택 (헤더/푸터 국가 셀렉터)
 *   2. Server-Timing 'country'                  Shopify 엣지 GeoIP. 동기 · 네트워크 0 · 대부분 여기서 끝
 *   3. sessionStorage 캐시된 감지값               폴백 경로 재요청 방지
 *   4. /browsing_context_suggestions.json       구형 Safari · Server-Timing 소멸 대비
 *   5. WookieDelivery.serverCountry             마켓 기본값 = 서버가 이미 렌더한 값
 *
 * ⚠️ 캐시 규칙: sessionStorage 에는 **실제 감지 성공값(2·4)만** 쓴다. 5번 폴백을 캐시하면
 *    "한 번 실패 → 서버값 저장 → 이후 캐시 적중 → 감지 영구 스킵" 이 되어 세션 내내 망가진다.
 *
 * ⚠️ Server-Timing 의 country 는 Shopify **비문서화 내부 헤더**다. 계약이 아니므로 사라질 수 있고,
 *    그래서 값 검증(2글자 + 매트릭스 등재)과 4번 폴백이 필수다.
 */
window.WookieDelivery = window.WookieDelivery || {};
(function (NS) {
  'use strict';

  var LS_EXPLICIT = 'wookie_ship_country';   // 명시적 선택 (영구)
  var SS_DETECTED = 'wookie_ship_detected';  // 감지 성공값 (세션)
  var PENDING_CLASS = 'delivery-pending';

  // money_format 토큰 → [소수자리, 천단위 구분자, 소수점 구분자]
  var MONEY_TOKENS = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_with_space_separator: [2, ' ', ','],
    amount_no_decimals_with_space_separator: [0, ' ', ',']
  };

  var lastAppliedCountry = null;

  // ── 저장소 (프라이빗 모드에서 throw 할 수 있어 전부 감싼다) ──────────────────────
  function readStore(store, key) {
    try { return (window[store] && window[store].getItem(key)) || ''; } catch (e) { return ''; }
  }
  function writeStore(store, key, value) {
    try { window[store].setItem(key, value); } catch (e) { /* private mode */ }
  }

  // ── 국가 코드 검증: 형식 + 매트릭스 등재 여부 ───────────────────────────────────
  function normalize(code) {
    var c = String(code || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(c) ? c : '';
  }
  function isKnown(code) {
    var c = normalize(code);
    if (!c || !NS.matrix) return false;
    return NS.matrix.allCountryCodes().indexOf(c) !== -1;
  }

  /**
   * Shopify 엣지가 붙여주는 `Server-Timing: country;desc="KR"` 을 읽는다.
   * 브라우저별로 속성 자체가 없거나, 빈 배열이거나, 항목 형태가 다를 수 있으므로 전 구간 방어한다.
   * 실패는 예외가 아니라 빈 문자열 — 호출부가 다음 우선순위로 내려간다.
   */
  function readServerTimingCountry() {
    try {
      if (!window.performance || typeof performance.getEntriesByType !== 'function') return '';
      var navs = performance.getEntriesByType('navigation');
      if (!navs || typeof navs.length !== 'number' || !navs.length) return '';
      var st = navs[0] && navs[0].serverTiming;
      if (!st || typeof st.length !== 'number') return '';
      for (var i = 0; i < st.length; i++) {
        var it = st[i];
        if (it && it.name === 'country' && typeof it.description === 'string') {
          return normalize(it.description);
        }
      }
      return '';
    } catch (e) {
      return '';
    }
  }

  // ── 통화 포맷 — 서버의 money 필터와 표기를 일치시킨다 ──────────────────────────
  // 입력은 '주요 단위'(예 19.99). 매트릭스가 센트가 아니라 주요 단위로 저장하기 때문이다.
  function formatMoney(amount, currency) {
    // 프로필 금액은 스토어 기준통화. 다통화 표시면 Shopify.currency 로 환산(무료배송바와 동일 규약).
    var cur = (window.Shopify && window.Shopify.currency) || {};
    var rate = parseFloat(cur.rate);
    var value = amount;
    var useCurrencyFormat = false;
    if (!isNaN(rate) && rate > 0 && cur.active) {
      value = amount * rate;
      // 표시통화가 프로필 통화와 다르면 통화코드를 함께 보여줘야 오해가 없다.
      if (currency && cur.active !== currency) useCurrencyFormat = true;
    }

    var fmt = (useCurrencyFormat ? NS.moneyWithCurrencyFormat : NS.moneyFormat) || NS.moneyFormat;
    if (!fmt) return String(Math.round(value * 100) / 100);

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (whole, token) {
      var spec = MONEY_TOKENS[token];
      if (!spec) return whole;
      var parts = value.toFixed(spec[0]).split('.');
      var intPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + spec[1]);
      return parts[1] ? intPart + spec[2] + parts[1] : intPart;
    });
  }

  // ── 적용 ─────────────────────────────────────────────────────────────────────
  /**
   * 임계금액 스팬 텍스트 동기화. 서버 렌더값을 스팬마다 1회 보존해 두고, free_over 가 없는
   * 국가면 그 값으로 되돌린다(안 그러면 이전 국가 금액이 잔류해 오정보가 된다).
   * 티커 슬라이더가 런타임에 스팬을 복제하므로 이 함수는 여러 번 호출돼도 안전해야 한다.
   */
  function updateThresholdSpans(payload) {
    var txt = (payload.freeOver != null && payload.freeOver > 0)
      ? formatMoney(payload.freeOver, payload.currency)
      : null;
    document.querySelectorAll('.delivery-free-threshold').forEach(function (s) {
      if (s.dataset.deliveryDefault == null) s.dataset.deliveryDefault = s.textContent;
      s.textContent = txt != null ? txt : s.dataset.deliveryDefault;
    });
  }

  function payloadFor(country) {
    var goal = NS.matrix ? NS.matrix.lookup(country) : null;
    var econ = (goal && goal.economy) || {};
    return {
      country: country,
      freeOver: (econ.free_over != null ? econ.free_over : null),
      currency: econ.currency
    };
  }

  /**
   * 국가 적용.
   *   isPreview=false  결정된 배송 국가를 갱신한다(NS.country).
   *   isPreview=true   상품페이지 셀렉터의 '만약 이 나라라면?' 표시용. NS.country 는 건드리지 않고
   *                    NS.preview 에만 얹는다 — 새로고침하면 감지 국가로 돌아가야 하기 때문이다.
   *
   * **publish 는 멱등** — 실효 국가가 그대로면 다시 쏘지 않는다(구독자 불필요 재렌더 방지).
   * 스팬 갱신은 매번 한다 — 티커 복제로 새 스팬이 생겼을 수 있기 때문이다.
   */
  function applyCountry(country, isPreview) {
    var c = normalize(country);
    if (!c) return;
    if (isPreview) {
      NS.preview = (c === NS.country) ? null : c;
    } else {
      NS.country = c;
    }

    var effective = NS.preview || NS.country;
    var changed = effective !== lastAppliedCountry;
    lastAppliedCountry = effective;

    var payload = payloadFor(effective);
    // 카트 AJAX 가 free-delivery-bar 를 innerHTML 째 갈아끼워도 새 인스턴스가 집어갈 수 있게 캐시한다
    // (pubsub 은 리플레이가 없다). 수신부: assets/global.js freeDeliveryBar connectedCallback.
    NS.lastFreeGoal = payload;
    if (changed && typeof publish === 'function') {
      publish('delivery:free-goal', payload);
    }
    updateThresholdSpans(payload);
  }

  function reveal() {
    document.documentElement.classList.remove(PENDING_CLASS);
  }

  function settle(country) {
    applyCountry(country);
    reveal();
  }

  // ── 국가 결정 ────────────────────────────────────────────────────────────────
  function resolve() {
    // 1. 명시적 선택 — 감지보다 항상 우선한다.
    var explicit = readStore('localStorage', LS_EXPLICIT);
    if (isKnown(explicit)) { settle(explicit); return; }

    // 2. Shopify 엣지 GeoIP (동기, 네트워크 0)
    var edge = readServerTimingCountry();
    if (isKnown(edge)) {
      writeStore('sessionStorage', SS_DETECTED, edge);
      settle(edge);
      return;
    }

    // 3. 이번 세션에서 이미 감지에 성공했다면 재요청하지 않는다.
    var cached = readStore('sessionStorage', SS_DETECTED);
    if (isKnown(cached)) { settle(cached); return; }

    // 4. 폴백: Shopify 네이티브 지오 엔드포인트 (구형 Safari 등)
    fetch('/browsing_context_suggestions.json?country[enabled]=true')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var d = data && data.detected_values && data.detected_values.country;
        var code = normalize(d && d.handle);
        if (isKnown(code)) {
          writeStore('sessionStorage', SS_DETECTED, code);
          settle(code);
        } else {
          // 5. 감지 실패 → 서버가 렌더한 마켓 기본값 그대로. **캐시하지 않는다.**
          settle(NS.serverCountry);
        }
      })
      .catch(function () { settle(NS.serverCountry); });
  }

  // ── 부트 ─────────────────────────────────────────────────────────────────────
  // 매트릭스 원문(NS.matrixRaw)은 데이터 계층 delivery-matrix.js 가 DOM 에서 1회 읽어 둔다.
  if (!NS.Matrix || !NS.matrixRaw) { reveal(); return; }

  NS.matrix = NS.Matrix.create({ matrixRaw: NS.matrixRaw });
  NS.formatMoney = formatMoney;
  NS.applyCountry = applyCountry;

  resolve();

  // 티커 슬라이더가 load 시점에 스팬을 복제하는 경우가 있어 한 번 더 훑는다.
  // publish 는 멱등이라 다시 나가지 않고, 미리보기가 켜져 있으면 실효 국가가 그대로라 덮지 않는다.
  window.addEventListener('load', function () {
    if (NS.country) applyCountry(NS.country);
  });
})(window.WookieDelivery);
